import { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'
import type {
  BookingCard,
  BookOutingResponse,
  CreateOutingBody,
  Outing,
  OutingInviteStatus,
  OutingResponse,
  OutingsResponse,
} from '../lib/types'

/**
 * DECLARED HERE RATHER THAN IN `lib/types.ts`, exactly as `useRoundHistory`
 * declares the record's shapes and `useClubhouseBook` the feed's — and for the
 * same reason. That file is copied VERBATIM into the Caddie app by
 * `bin/mobile contract`, banner and all, so anything put there lands in the
 * app's source tree whether the app wants it or not. Caddie imports courses,
 * conditions, wind and the member from that copy; it has never had an outing,
 * an invitation or a re-booking, and a screen on the web club is no reason to
 * give it one.
 */
/**
 * One person carried over from the outing being re-booked, with what they said
 * LAST time. `previous_status` is context for the organizer — the new outing
 * inherits nothing, because nobody has been asked yet.
 */
export interface RebookingInvite {
  email: string | null
  phone: string | null
  contact: string
  previous_status: OutingInviteStatus | null
}

/**
 * The next outing, composed out of a past one
 * (GET /golf/outings/:id/rebooking).
 *
 * A DRAFT, never a record: nothing has been written and nothing is reserved.
 * Every field is a starting point for the ordinary create, and two of them are
 * guesses that ship with what they were guessed from:
 *
 *   * `tee_off_at` is the same weekday and hour, next time round — arithmetic,
 *     not a tee sheet. Nobody has asked the course whether that hour is for
 *     sale; `booking_url` links to that day's sheet so the member can, and
 *     `booking_window_days` says how far out the course books at all.
 *   * `price_cents_per_head` is what the SOURCE outing recorded. A rate is a
 *     property of a sheet that moves; this one is a property of history.
 */
export interface Rebooking {
  source_outing_id: string
  source_tee_off_at: string | null
  course_key: string
  course_name: string | null
  tee_off_at: string | null
  holes: number | null
  slots: number
  price_cents_per_head: number | null
  booking_url: string | null
  booking_window_days: number | null
  invites: RebookingInvite[]
}

export interface RebookingResponse {
  rebooking: Rebooking
}

/**
 * Which half of the book to open. Omitting it is the whole book, newest-
 * proposed first — the order this endpoint has always answered in, and the one
 * the outings desk reads.
 */
export type OutingScope = 'past' | 'upcoming'

/**
 * The signed-in member's outings (GET /golf/outings) plus the mutations of the
 * tee-time MVP: create, finalize and book. Deliberately thin — the server owns
 * the partition, the ordering and the status; the hook only keeps the list
 * current.
 *
 * Fetches only while `enabled` (the viewer is signed in) — the endpoints are
 * auth-gated and a visitor's console is a sign-in invitation, not a 401.
 */
export function useOutings(enabled: boolean, scope?: OutingScope) {
  const [outings, setOutings] = useState<Outing[]>([])
  const [isLoading, setIsLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!enabled) return
    try {
      const path = scope ? `/golf/outings?scope=${scope}` : '/golf/outings'
      const data = await api.get<OutingsResponse>(path)
      setOutings(data.outings)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your outings.')
    } finally {
      setIsLoading(false)
    }
  }, [enabled, scope])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch-on-mount, the same accepted pattern as every house data hook (e.g. useClubMembership)
    void refresh()
  }, [refresh])

  /**
   * POST /golf/outings — prepends the new outing (the unscoped list is
   * newest-first).
   *
   * A SCOPED list is left alone, because the answer is already right: a
   * just-proposed outing tees off in the future, so it belongs to `upcoming`
   * and cannot belong to `past`. Prepending it regardless would put next
   * Saturday at the top of a member's history.
   */
  const create = useCallback(
    async (body: CreateOutingBody): Promise<Outing> => {
      const data = await api.post<OutingResponse>('/golf/outings', body)
      if (!scope) setOutings((prev) => [data.outing, ...prev])
      return data.outing
    },
    [scope],
  )

  /** POST /golf/outings/:id/finalize — swaps in the server's updated outing. */
  const finalize = useCallback(async (id: string, bookingRef?: string): Promise<Outing> => {
    const data = await api.post<OutingResponse>(
      `/golf/outings/${id}/finalize`,
      bookingRef ? { booking_ref: bookingRef } : {},
    )
    setOutings((prev) => prev.map((o) => (o.id === data.outing.id ? data.outing : o)))
    return data.outing
  }, [])

  /**
   * POST /golf/outings/:id/book — the club makes the reservation itself.
   * The card goes straight out in the request and is never held here: no
   * state, no ref, nothing that survives the call.
   */
  const book = useCallback(async (id: string, card: BookingCard): Promise<BookOutingResponse> => {
    const data = await api.post<BookOutingResponse>(`/golf/outings/${id}/book`, { card })
    setOutings((prev) => prev.map((o) => (o.id === data.outing.id ? data.outing : o)))
    return data
  }, [])

  /**
   * GET /golf/outings/:id/rebooking — the next outing, composed out of this
   * one. A READ: nothing is written and nothing is reserved, so this never
   * touches the list. The member edits the draft and proposes it through
   * `create`, the same door every other outing comes through.
   */
  const rebooking = useCallback(async (id: string): Promise<Rebooking> => {
    const data = await api.get<RebookingResponse>(`/golf/outings/${id}/rebooking`)
    return data.rebooking
  }, [])

  return {
    outings,
    isLoading: enabled ? isLoading : false,
    error,
    refresh,
    create,
    finalize,
    book,
    rebooking,
  }
}
